import React, { useCallback, useEffect, useRef, useState } from "react";
import Animated, {
  Easing,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { GetThemeValueForKey, XStack } from "tamagui";

import SyncIcon from "@/components/SyncIcon";
import { Text } from "@/components/Typography";

interface TextTransitionAnimationProps {
  isVisible: boolean;
  texts: string[];
  onComplete?: () => void;
  transitionDuration?: number;
  cycleDuration?: number;
  initialDelay?: number;
  textStyle?: {
    color?: string;
    fontSize?: GetThemeValueForKey<"fontSize"> | "unset";
  };
  icon?: React.ComponentType<any> | null;
  iconProps?: any;
}

const DEFAULT_TRANSITION_DURATION = 300;
const DEFAULT_CYCLE_DURATION = 2000;
const DEFAULT_INITIAL_DELAY = 500;
const FADE_OUT_DURATION = 200;

const TextTransitionAnimation = ({
  isVisible,
  texts,
  onComplete,
  transitionDuration = DEFAULT_TRANSITION_DURATION,
  cycleDuration = DEFAULT_CYCLE_DURATION,
  initialDelay = DEFAULT_INITIAL_DELAY,
  textStyle = {},
  icon = SyncIcon,
  iconProps = {},
}: TextTransitionAnimationProps) => {
  const reducedMotion = useReducedMotion();
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [isStarted, setIsStarted] = useState(false);

  const opacity = useSharedValue(0);
  const translateY = useSharedValue(0);

  const timeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearAllTimers = useCallback(() => {
    timeoutRefs.current.forEach(clearTimeout);
    timeoutRefs.current = [];
  }, []);

  const transitionToNextText = useCallback(
    (nextIndex: number) => {
      if (reducedMotion) {
        setCurrentTextIndex(nextIndex);
        return;
      }

      translateY.value = withTiming(-20, {
        duration: transitionDuration / 2,
        easing: Easing.out(Easing.quad),
      });

      opacity.value = withTiming(0, {
        duration: transitionDuration / 2,
        easing: Easing.out(Easing.quad),
      });

      const transitionTimeout = setTimeout(() => {
        setCurrentTextIndex(nextIndex);

        translateY.value = 15;
        translateY.value = withTiming(0, {
          duration: transitionDuration / 2,
          easing: Easing.out(Easing.quad),
        });

        opacity.value = withTiming(1, {
          duration: transitionDuration / 2,
          easing: Easing.out(Easing.quad),
        });
      }, transitionDuration / 2);

      timeoutRefs.current.push(transitionTimeout);
    },
    [reducedMotion, translateY, opacity, transitionDuration],
  );

  useEffect(() => {
    if (!isVisible || texts.length === 0) {
      clearAllTimers();
      setIsStarted(false);
      setCurrentTextIndex(0);
      opacity.value = 0;
      translateY.value = 0;
      return;
    }

    if (reducedMotion) {
      setIsStarted(true);
      setCurrentTextIndex(0);
      opacity.value = 1;
      return;
    }

    const startTimeout = setTimeout(() => {
      setIsStarted(true);

      opacity.value = withTiming(1, {
        duration: transitionDuration,
        easing: Easing.out(Easing.quad),
      });

      for (let i = 1; i < texts.length; i++) {
        const transitionTimeout = setTimeout(() => {
          transitionToNextText(i);
        }, cycleDuration * i);
        timeoutRefs.current.push(transitionTimeout);
      }
    }, initialDelay);

    timeoutRefs.current.push(startTimeout);

    return clearAllTimers;
  }, [
    isVisible,
    texts,
    reducedMotion,
    opacity,
    translateY,
    clearAllTimers,
    transitionToNextText,
    cycleDuration,
    transitionDuration,
    initialDelay,
  ]);

  useEffect(() => {
    if (!isVisible && isStarted) {
      if (reducedMotion) {
        onComplete?.();
        return;
      }

      translateY.value = withTiming(-20, {
        duration: FADE_OUT_DURATION,
        easing: Easing.out(Easing.quad),
      });

      opacity.value = withTiming(0, {
        duration: FADE_OUT_DURATION,
        easing: Easing.out(Easing.quad),
      });

      const completeTimeout = setTimeout(() => {
        onComplete?.();
      }, FADE_OUT_DURATION);

      timeoutRefs.current.push(completeTimeout);
    }
  }, [isVisible, isStarted, opacity, translateY, onComplete, reducedMotion]);

  const animatedStyle = useAnimatedStyle(
    () => ({
      opacity: opacity.value,
      transform: [{ translateY: translateY.value }],
    }),
    [opacity, translateY],
  );

  if (!isStarted || texts.length === 0) return null;

  const currentText = texts[currentTextIndex];

  const CurrentIcon = icon;

  const { color = "$textPrimary", fontSize = "$sm" } = textStyle;

  if (reducedMotion) {
    return (
      <XStack alignItems="center" gap="$150">
        {CurrentIcon && <CurrentIcon color={color} {...iconProps} />}
        <Text color={color} fontSize={fontSize}>
          {currentText}
        </Text>
      </XStack>
    );
  }

  return (
    <Animated.View style={animatedStyle}>
      <XStack alignItems="center" gap="$150">
        {CurrentIcon && <CurrentIcon color={color} {...iconProps} />}
        <Text color={color} fontSize={fontSize}>
          {currentText}
        </Text>
      </XStack>
    </Animated.View>
  );
};

export default TextTransitionAnimation;
